import { useEffect, useState, useCallback } from "react";
import { Label } from "@progress/kendo-react-labels";
import { TextArea } from "@progress/kendo-react-inputs";
import { Tile } from "components/UIComponents/Tile";
import styles from "./MatterDetails.module.scss";
import { FormComboBox, FormInput } from "components/Matters/index";
import { useBranches } from "services/branchService";
import { CONSTANT } from "CONSTANT/Matter/MatterForm";
import { filterBy } from "@progress/kendo-data-query";
import {
  addMatter,
  getActiveApplications,
  getMatterRefExample,
  getWorkType,
  updateOnboarding,
} from "api/index";
import { useCurrentUser } from "services/currentUserService";
import { useActiveFeeEarner } from "services/userService";
import { useActiveDepartments } from "services/departmentService";
import { useActiveWorkTypes } from "services/workTypeService";

const MatterDetails = ({ moveNext, formData, setHandlefn }) => {
  const { data: branchesList, loading: branchesLoading } = useBranches();
  const { data: user } = useCurrentUser();
  const { data: feeearnerData } = useActiveFeeEarner();
  const { data: departmentData } = useActiveDepartments();
  const { data: categoryData } = useActiveWorkTypes();

  const [details, setDetails] = useState(formData.matterDetails?.details || "");
  const [departmentFilter, setDepartmentFilter] = useState(
    departmentData?.slice() || []
  );
  const [categoryFilter, setCategoryFilter] = useState(
    categoryData?.slice() || []
  );
  const [superVisor, setSuperVisor] = useState(feeearnerData?.slice() || []);
  const [feeEarner, setFeeEarner] = useState(feeearnerData?.slice() || []);
  const [application, setApplication] = useState([]);
  const [applicationFilter, setApplicationFilter] = useState([]);
  const [validationState, setValidationState] = useState(false);

  const [inputState, setInputState] = useState({
    [CONSTANT.INPUT.MATTER_REF]: formData.matterDetails?.matterRef || "",
  });

  const [comboBoxState, setComboBoxState] = useState({
    [CONSTANT.COMBOBOX.SUPERVISIOR]: formData.matterDetails?.Supervisior || "",
    [CONSTANT.COMBOBOX.FEE_EARNER]:
      formData.matterDetails?.Fee_Earner ||
      (user?.isFeeEarner
        ? {
            userRef: user?.userRef,
            display: `${user?.userRef} - ${user.displayname} `,
          }
        : null),
    [CONSTANT.COMBOBOX.APPLICATION]: formData.matterDetails?.Application || "",
    [CONSTANT.COMBOBOX.DEPARTMENT]: formData.matterDetails?.Department || "",
    [CONSTANT.COMBOBOX.CATEGORY]: formData.matterDetails?.Work_Type || "",
    [CONSTANT.COMBOBOX.BRANCH]:
      formData.matterDetails?.Branch ||
      branchesList.find(
        (branch) =>
          branch.branchId ===
            formData.clientData?.individual?.branch?.branchId ||
          branch.branchId === formData.clientData?.company?.branch?.branchId
      ),
    [CONSTANT.COMBOBOX.RATE_TYPE]: formData.matterDetails?.Rate_Type || "",
  });

  useEffect(() => {
    getApplication();
    fectchMatterRef(formData.clientData?.clientRef);
  }, []);

  useEffect(() => {
    if (feeearnerData) {
      setFeeEarner(feeearnerData);
      setSuperVisor(feeearnerData);
    }
  }, [feeearnerData?.length]);

  useEffect(() => {
    if (departmentData) {
      setDepartmentFilter(departmentData);
    }
  }, [departmentData]);
  useEffect(() => {
    if (categoryData) {
      setCategoryFilter(categoryData);
    }
  }, [categoryData]);

  const handleComboBoxChange = (e) => {
    setComboBoxState({
      ...comboBoxState,
      [e.target.name]: e.target.value,
    });
  };

  const handleInputChange = (value, label) => {
    setInputState({
      ...inputState,
      [label]: value,
    });
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      let hasError = false;

      if (
        !inputState[CONSTANT.INPUT.MATTER_REF] ||
        !details ||
        !comboBoxState[CONSTANT.COMBOBOX.DEPARTMENT] ||
        !comboBoxState[CONSTANT.COMBOBOX.CATEGORY]
      ) {
        hasError = true;
      }

      setValidationState(hasError);

      if (!hasError) {
        const dataObject = {
          matterRef: inputState[CONSTANT.INPUT.MATTER_REF] || null,
          clientId: formData.clientData?.clientId,
          details: details,
          feeEarnerRef:
            comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER].userRef || null,
          supervisorRef:
            comboBoxState[CONSTANT.COMBOBOX.SUPERVISIOR]?.userRef || null,
          appCode: comboBoxState[CONSTANT.COMBOBOX.APPLICATION].appCode || null,
          branchId: comboBoxState[CONSTANT.COMBOBOX.BRANCH]?.branchId || null,
          application:
            comboBoxState[CONSTANT.COMBOBOX.APPLICATION].appName || null,
          departmentId:
            comboBoxState[CONSTANT.COMBOBOX.DEPARTMENT]?.departmentID || -1,
          workTypeId:
            comboBoxState[CONSTANT.COMBOBOX.CATEGORY]?.workTypeID || -1,
          rateType:
            comboBoxState[CONSTANT.COMBOBOX.RATE_TYPE]?.rateType || null,
          status: "Onboarding",
        };

        try {
          const res = await addMatter({ data: dataObject });
          if (res?.status === 200) {
            console.log("Matter Added Successfully");
            moveNext(
              {},
              {
                matterDetails: {
                  ...comboBoxState,
                  matterId: res.data.data?.matterId,
                  details: details,
                  matterRef: inputState[CONSTANT.INPUT.MATTER_REF],
                  appCode: comboBoxState[CONSTANT.COMBOBOX.APPLICATION].appCode,
                  client: {
                    clientId: formData.clientData?.clientId,
                    clientRef: formData.clientData?.clientRef,
                    clientName: formData.clientData?.clientName,
                  },
                },
              }
            );
            updateOnboarding(
              { matterId: res.data.data?.matterId },
              formData.onboardingId
            );
          }
        } catch (err) {
          if (
            err?.response?.data !== "" &&
            err?.response?.data?.errorMessage?.includes(
              "The duplicate key value is"
            )
          ) {
            console.log("Matter Ref Already Added");
          } else {
            console.log("Error Occured");
          }
        }
      }
    },
    [inputState, details, comboBoxState, formData, moveNext]
  );

  useEffect(() => {
    if (setHandlefn) setHandlefn(() => handleSubmit);
  }, [setHandlefn, handleSubmit]);

  const filterFeeEarner = (filter) => {
    const data = feeearnerData?.slice();
    return filterBy(data, filter);
  };
  const feeEarnerfilterChange = (event) => {
    setFeeEarner(filterFeeEarner(event.filter));
  };

  const filterSuperVisor = (filter) => {
    const data = feeearnerData?.slice();
    return filterBy(data, filter);
  };
  const superVisorfilterChange = (event) => {
    setSuperVisor(filterSuperVisor(event.filter));
  };

  const filterrApplication = (filter) => {
    const data = application.slice();
    return filterBy(data, filter);
  };
  const applicationfilterChange = (event) => {
    setApplicationFilter(filterrApplication(event.filter));
  };

  const filterDepartment = (filter) => {
    const data = departmentData.slice();
    return filterBy(data, filter);
  };
  const departmentfilterChange = (event) => {
    setDepartmentFilter(filterDepartment(event.filter));
  };

  const filterCategory = (filter) => {
    const data = categoryData.slice();
    return filterBy(data, filter);
  };
  const categoryFilterChange = (event) => {
    setCategoryFilter(filterCategory(event.filter));
  };

  const getApplication = async () => {
    const datas = await getActiveApplications();
    const preparedData = datas?.data?.data;
    setApplication(preparedData);
    setApplicationFilter(preparedData);
  };

  const fectchMatterRef = async (value) => {
    if (value !== null) {
      try {
        const response = await getMatterRefExample(
          formData.clientData?.clientRef
        );
        setInputState({
          ...inputState,
          [CONSTANT.INPUT.MATTER_REF]: response.data,
        });
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (comboBoxState[CONSTANT.COMBOBOX.CATEGORY]?.workTypeID) {
      getWorkTypeData();
    }
  }, [comboBoxState[CONSTANT.COMBOBOX.CATEGORY]?.workTypeID]);

  const getWorkTypeData = async () => {
    const { data } = await getWorkType(
      comboBoxState[CONSTANT.COMBOBOX.CATEGORY]?.workTypeID
    );
    const response = data.data;

    const appItem = application.find(
      (item) => item.appCode === response.appCode
    );
    if (!formData.matterDetails?.Application) {
      if (!appItem || !response.appCode) {
        setComboBoxState((prevState) => ({
          ...prevState,
          [CONSTANT.COMBOBOX.APPLICATION]: "",
        }));
        return;
      }
      setComboBoxState((prevState) => ({
        ...prevState,
        [CONSTANT.COMBOBOX.APPLICATION]: {
          appCode: response?.appCode,
          appName: appItem?.appName,
        },
      }));
    }
  };

  return (
    <Tile
      heading="Matter Details"
      className="overflow-auto px-4 h-100 bg-transparent border-0 border w-100 shadow-none "
    >
      <div style={{ height: "calc(100% - 70px)" }}>
        <form onSubmit={handleSubmit} className="h-100" noValidate>
          <div
            className="overflow-auto"
            style={{ height: "calc(100% - 55px)" }}
          >
            <div className={`${styles.container}`}>
              <div className="mt-3 col-xl-10 col-12 rowContainer">
                <div className="d-flex w-100 flex-column">
                  <Label className={`label mb-1`}>Matter Details</Label>
                  <TextArea
                    placeholder="Enter Matter Details"
                    autoSize={true}
                    value={details}
                    className={`${styles[`matter-details-container`]} ${
                      validationState && !details ? styles.error : ""
                    }`}
                    onChange={(e) => setDetails(e.value)}
                  />
                </div>
              </div>

              <div className="mt-3 col-xl-10 col-12 rowContainer">
                <FormComboBox
                  wf={true}
                  isStyled={true}
                  width={"260px"}
                  onChangeComboBox={handleComboBoxChange}
                  onFilterChange={feeEarnerfilterChange}
                  data={feeEarner}
                  textField="display"
                  state={comboBoxState}
                  name={CONSTANT.COMBOBOX.FEE_EARNER}
                  val={comboBoxState[CONSTANT.COMBOBOX.FEE_EARNER]}
                />
                <FormComboBox
                  wf={true}
                  isStyled={true}
                  width={"260px"}
                  onChangeComboBox={handleComboBoxChange}
                  textField="name"
                  onFilterChange={departmentfilterChange}
                  data={departmentFilter}
                  state={comboBoxState}
                  name={CONSTANT.COMBOBOX.DEPARTMENT}
                  val={comboBoxState[CONSTANT.COMBOBOX.DEPARTMENT]}
                  inputClassName={` ${
                    validationState &&
                    !comboBoxState[CONSTANT.COMBOBOX.DEPARTMENT]
                      ? styles.error
                      : ""
                  }`}
                />
              </div>

              <div className="mt-3 col-xl-10 col-12 rowContainer">
                <FormComboBox
                  wf={true}
                  isStyled={true}
                  width={"260px"}
                  onChangeComboBox={handleComboBoxChange}
                  onFilterChange={superVisorfilterChange}
                  data={superVisor}
                  textField="displayname"
                  state={comboBoxState}
                  name={CONSTANT.COMBOBOX.SUPERVISIOR}
                  val={comboBoxState[CONSTANT.COMBOBOX.SUPERVISIOR]}
                />

                <FormComboBox
                  wf={true}
                  isStyled={true}
                  width={"260px"}
                  onChangeComboBox={handleComboBoxChange}
                  onFilterChange={categoryFilterChange}
                  data={categoryFilter.filter(
                    (item) =>
                      item?.departmentId ===
                      comboBoxState[CONSTANT.COMBOBOX.DEPARTMENT]?.departmentID
                  )}
                  state={comboBoxState}
                  textField="name"
                  name={CONSTANT.COMBOBOX.CATEGORY}
                  inputClassName={`${
                    validationState &&
                    !comboBoxState[CONSTANT.COMBOBOX.CATEGORY]
                      ? styles.error
                      : ""
                  }`}
                />
              </div>

              <div className="mt-3 col-xl-10 col-12 rowContainer">
                <FormInput
                  labelWidth
                  wf={true}
                  labelForComboBox={"Matter Ref"}
                  isStyled={true}
                  width={"260px"}
                  state={inputState}
                  label={CONSTANT.INPUT.MATTER_REF}
                  onChangeInput={handleInputChange}
                  inputClassName={` ${
                    validationState && !inputState[CONSTANT.INPUT.MATTER_REF]
                      ? styles.error
                      : ""
                  }`}
                />

                <FormComboBox
                  onChangeComboBox={handleComboBoxChange}
                  wf={true}
                  isStyled={true}
                  width={"260px"}
                  onFilterChange={applicationfilterChange}
                  textField="display"
                  data={applicationFilter}
                  state={comboBoxState}
                  name={CONSTANT.COMBOBOX.APPLICATION}
                  val={comboBoxState[CONSTANT.COMBOBOX.APPLICATION]}
                />
              </div>

              <div className="mt-3 col-xl-10 col-12 rowContainer">
                <FormComboBox
                  isStyled={true}
                  wf={true}
                  width={"260px"}
                  placeholder={"Rate Type"}
                  iconClassName="wa"
                  data={[
                    {
                      value: "Fee Earner Default",
                      rateType: "FeeEarnerDefault",
                    },
                    {
                      value: "Matter Flat Rate",
                      rateType: "MatterFlatRate",
                    },
                    {
                      value: "Rate Category",
                      rateType: "RateCategory",
                    },
                  ]}
                  textField="value"
                  state={comboBoxState}
                  name={CONSTANT.COMBOBOX.RATE_TYPE}
                  onChangeComboBox={handleComboBoxChange}
                  val={comboBoxState[CONSTANT.COMBOBOX.RATE_TYPE]}
                />
                <FormComboBox
                  isStyled={true}
                  wf={true}
                  data={branchesList || []}
                  textField="branchName"
                  width={"260px"}
                  boxShadow={true}
                  state={comboBoxState}
                  name={CONSTANT.COMBOBOX.BRANCH}
                  onChangeComboBox={handleComboBoxChange}
                  disabled={branchesLoading}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </Tile>
  );
};

export default MatterDetails;
